/* Root Cause Analysis donut — drawn from the same {label, pct} themes the PDF's
   matplotlib donut is built from. Pure SVG (no chart library in this app), using
   the slice colours the backend already assigned so the legend and the PDF agree. */
import type { DraftGenerated } from '../../services/api';

/* A chart legend is not prose — nothing renders **bold** here, so the asterisks
   print literally. The backend strips them now, but drafts generated before that fix
   still carry them, and a saved draft is not regenerated on read. */
function cleanLabel(label: string): string {
  return label.replace(/[*_`]+/g, '').trim();
}

const FALLBACK_COLOURS = [
  'var(--fail)',
  'var(--accent)',
  'var(--watch)',
  'var(--info)',
  'var(--pass)',
];

const SIZE = 190;
const R = 68; // radius of the ring's centre-line
const STROKE = 34;
const CIRCUMFERENCE = 2 * Math.PI * R;

export function RootCauseDonut({
  themes,
}: {
  themes: DraftGenerated['root_cause_themes'];
}): JSX.Element {
  if (!themes || themes.length === 0) {
    return <p className="dr-unfilled">No root-cause themes identified.</p>;
  }

  const total = themes.reduce((sum, t) => sum + (t.pct || 0), 0) || 1;

  // Walk the ring, converting each theme's share into a dash segment.
  let offset = 0;
  const slices = themes.map((t, i) => {
    const share = (t.pct || 0) / total;
    const length = share * CIRCUMFERENCE;
    // Mid-angle of this slice, in degrees clockwise from 12 o'clock — where the
    // percentage label sits, as it does on the PDF's chart.
    const midDeg = ((offset + length / 2) / CIRCUMFERENCE) * 360 - 90;
    const midRad = (midDeg * Math.PI) / 180;
    const slice = {
      key: t.label,
      pct: Math.round(t.pct || 0),
      colour: t.colour || FALLBACK_COLOURS[i % FALLBACK_COLOURS.length],
      dash: `${length} ${CIRCUMFERENCE - length}`,
      // -90° so the first slice starts at 12 o'clock rather than 3 o'clock.
      offset: -offset,
      labelX: SIZE / 2 + R * Math.cos(midRad),
      labelY: SIZE / 2 + R * Math.sin(midRad),
      // A label needs a slice wide enough to sit in; below that it collides with its
      // neighbours and reads as noise. The legend still carries the number.
      showLabel: share >= 0.08,
    };
    offset += length;
    return slice;
  });

  return (
    <div className="dr-donut">
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`Root cause themes: ${themes
          .map((t) => `${cleanLabel(t.label)} ${t.pct}%`)
          .join(', ')}`}
      >
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {slices.map((s) => (
            <circle
              key={s.key}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              stroke={s.colour}
              strokeWidth={STROKE}
              strokeDasharray={s.dash}
              strokeDashoffset={s.offset}
            />
          ))}
        </g>
        {/* Percentages sit ON the slices, as they do on the PDF's chart — a legend
            alone makes the reader look up every share. Not inside the rotated group:
            the text must stay upright. */}
        {slices.filter((s) => s.showLabel).map((s) => (
          <text
            key={`${s.key}-pct`}
            x={s.labelX}
            y={s.labelY}
            textAnchor="middle"
            dominantBaseline="central"
            fill="#FFFFFF"
            fontSize="11"
            fontWeight="700"
          >
            {s.pct}%
          </text>
        ))}
      </svg>

      <ul className="dr-donut-legend">
        {themes.map((t, i) => (
          <li key={t.label}>
            <span
              className="dr-swatch"
              style={{
                background:
                  t.colour || FALLBACK_COLOURS[i % FALLBACK_COLOURS.length],
              }}
            />
            {cleanLabel(t.label)}
            <span className="t-muted"> · {Math.round(t.pct)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
